import React, { useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFaceSmileRelaxed } from '@fortawesome/pro-light-svg-icons';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import LikertFaceScale from '../LikertFaceScale';
import { FormTextField } from '../Form/FormTextField';
import Loader from '../Loader';
import { FEEDBACK_DETAIL_MAX_LENGTH, FEEDBACK_LOW_RATINGS } from '../constants';
import { postData } from '../../../DataAccessLayer';
import { feedback as feedbackService } from '../../../DataAccessLayer/services';
import { getBrowserMetadata } from '../../../DataAccessLayer/util';

/**
 * One written answer, under its own label, held to the length the backend keeps.
 */
const DetailField = ({ id, label, value, onChange }) => (
  <FormTextField
    id={id}
    label={label}
    value={value}
    onChange={(event) => onChange(event.target.value)}
    htmlInputProps={{ maxLength: FEEDBACK_DETAIL_MAX_LENGTH }}
    helperText={`${value.length}/${FEEDBACK_DETAIL_MAX_LENGTH}`}
    multiline
    minRows={3}
    fullWidth
  />
);

/**
 * What a learner has to say about myStudySidekick: how they find it, and
 * anything they have run into, thought of, or want to tell us.
 *
 * Each question opens only when it is asked for — a low rating asks what to
 * change, and each box below opens the field that goes with it — so the form
 * stays a single question until the learner has more to say.
 */
function FeedbackForm({ id = 'feedback' }) {
  /*
   * An LTI request carries a signed courseId the backend prefers; a portal
   * request has no other source for it, so the course on screen is sent along.
   */
  const courseId = useSelector((state) => state.course.courseId);
  const [rating, setRating] = useState(null);
  const [changeDetail, setChangeDetail] = useState('');
  const [isBugChecked, setIsBugChecked] = useState(false);
  const [pageUrl, setPageUrl] = useState('');
  const [bugDetail, setBugDetail] = useState('');
  const [isIdeaChecked, setIsIdeaChecked] = useState(false);
  const [ideaDetail, setIdeaDetail] = useState('');
  const [isOtherChecked, setIsOtherChecked] = useState(false);
  const [otherDetail, setOtherDetail] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Nothing to send is nothing to say: Submit waits until there is something.
  const hasSomethingToSay =
    rating !== null ||
    [changeDetail, bugDetail, ideaDetail, otherDetail].some((answer) => answer.trim());

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      await postData(feedbackService, {
        rating,
        changeDetail,
        pageUrl,
        bugDetail,
        ideaDetail,
        otherDetail,
        courseId,
        // The browser it was written in, so a bug can be reproduced somewhere.
        metadata: await getBrowserMetadata(),
      });
      setIsSubmitted(true);
    } catch (err) {
      console.log(err);
      toast.error('Failed to send feedback. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isSubmitted) {
    return (
      <Box className="feedbackThanks" id={`${id}__box_thanks`}>
        <FontAwesomeIcon icon={faFaceSmileRelaxed} size="4x" className="feedbackThanksIcon" />
        <Typography component="h3" className="feedbackThanksHeading">
          Thanks for the feedback!
        </Typography>
        <Typography className="feedbackThanksText">
          We&apos;re always striving to improve, and your input helps us do just that. So, thanks
          again for being awesome and sharing your thoughts with us!
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="feedbackForm" id={`${id}__box_form`}>
      <Loader id={`${id}__loader_submit`} isOpen={isSaving} />

      <Typography className="mss__label feedbackQuestion" component="p">
        How do you like myStudySidekick?
      </Typography>
      <LikertFaceScale id={id} rating={rating} onChange={setRating} />

      {/* Only a learner who is unhappy is asked what would make it better. */}
      {FEEDBACK_LOW_RATINGS.includes(rating) && (
        <DetailField
          id={`${id}__input_changeDetail`}
          label="What would you change?"
          value={changeDetail}
          onChange={setChangeDetail}
        />
      )}

      <FormControlLabel
        className="feedbackCheckbox"
        control={
          <Checkbox
            id={`${id}__checkbox_bug`}
            checked={isBugChecked}
            onChange={() => setIsBugChecked((checked) => !checked)}
          />
        }
        label="I found a bug"
      />
      {isBugChecked && (
        <>
          <FormTextField
            id={`${id}__input_pageUrl`}
            label="Which page?"
            value={pageUrl}
            onChange={(event) => setPageUrl(event.target.value)}
            htmlInputProps={{ maxLength: FEEDBACK_DETAIL_MAX_LENGTH }}
            fullWidth
          />
          <DetailField
            id={`${id}__input_bugDetail`}
            label="Tell us about the bug"
            value={bugDetail}
            onChange={setBugDetail}
          />
        </>
      )}

      <FormControlLabel
        className="feedbackCheckbox"
        control={
          <Checkbox
            id={`${id}__checkbox_idea`}
            checked={isIdeaChecked}
            onChange={() => setIsIdeaChecked((checked) => !checked)}
          />
        }
        label="I have an idea for something new"
      />
      {isIdeaChecked && (
        <DetailField
          id={`${id}__input_ideaDetail`}
          label="Tell us about your idea"
          value={ideaDetail}
          onChange={setIdeaDetail}
        />
      )}

      <FormControlLabel
        className="feedbackCheckbox"
        control={
          <Checkbox
            id={`${id}__checkbox_other`}
            checked={isOtherChecked}
            onChange={() => setIsOtherChecked((checked) => !checked)}
          />
        }
        label="I have something else to say"
      />
      {isOtherChecked && (
        <DetailField
          id={`${id}__input_otherDetail`}
          label="What else would you like to say?"
          value={otherDetail}
          onChange={setOtherDetail}
        />
      )}

      <Box className="mss__formActions feedbackActions">
        <Button
          variant="contained"
          className="mss__button primary"
          id={`${id}__button_submit`}
          disabled={!hasSomethingToSay || isSaving}
          onClick={handleSubmit}
        >
          Submit Feedback
        </Button>
      </Box>
    </Box>
  );
}

export default FeedbackForm;
